import genericDelete from "graphqlBase/__utils__/genericDelete";
import React, { useCallback, useState } from "react";
import { Typography } from "@mui/material";
import { useRecoilValue, useResetRecoilState } from "recoil";
import translations from "translations";
import { genericDeleteParams } from "components/GenericDelete/genericDeleteState";
import { WarningModal } from "@mlpa/core-ui-core-open_ocean_ui";

export interface ConfirmAndDeleteProps {
  title: string;
  message?: string;
  open: boolean;
}

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}
const { OK } = translations.globals.button;
const { generalErrorMessage } = translations.globals.popupMessages;

const ConfirmAndDelete: React.FC<ConfirmAndDeleteProps> = ({ title, message, open }) => {
  const deleteParams = useRecoilValue(genericDeleteParams);
  const resetDeleteParams = useResetRecoilState(genericDeleteParams);
  const [openWarningDelete, setOpenWarningDelete] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleDeleteInner = useCallback(() => {
    const { id, entity, afterDelete } = deleteParams;
    if (entity && id) {
      genericDelete({ entity })(id)
        .then((deletedIds) => {
          afterDelete?.(deletedIds);
        })
        .catch((e) => {
          const graphqlMessage = e?.graphQLErrors?.[0]?.message ?? e?.networkError?.result?.errors?.[0]?.message;
          setErrorMessage(graphqlMessage ?? (e instanceof Error ? e.message : null));
          setOpenWarningDelete(true);
        })
        .finally(() => {
          resetDeleteParams();
        });
    }
  }, [deleteParams, setOpenWarningDelete, resetDeleteParams]);

  if (openWarningDelete) {
    return (
      <WarningModal
        open={openWarningDelete}
        title={generalErrorMessage}
        width={830}
        variant="error"
        primaryButtonProps={{
          label: OK,
          onClick: () => setOpenWarningDelete(false),
        }}
        onClose={() => setOpenWarningDelete(false)}
      >
        <Typography sx={{ whiteSpace: "pre-line" }}>{errorMessage || ""}</Typography>
      </WarningModal>
    );
  }

  return (
    <WarningModal
      width="50rem"
      onClose={resetDeleteParams}
      primaryButtonProps={{
        label: deleteParams?.submitButtonText ?? translations.globals.button.delete,
        onClick: handleDeleteInner,
      }}
      secondaryButtonProps={{ label: translations.globals.button.cancel, onClick: resetDeleteParams }}
      variant="error"
      errorMessage={message ?? ""}
      title={title ?? ""}
      open={open}
      detailsShowLabel={deleteParams.detailsShowLabel}
      detailsHideLabel={deleteParams.detailsHideLabel}
      detailsContent={deleteParams.customModalContent}
    />
  );
};

export default ConfirmAndDelete;
